/**
 * Coachmark — focus-trapped tooltip rendered next to the active anchor.
 *
 * Positioning is computed against the anchor's bounding rect with
 * auto-flip — if the chosen placement would render off-screen at the
 * current viewport, fall back to the opposite side. Mobile (≤640px) uses
 * a docked-bottom placement regardless of step.placement so 390px viewports
 * stay readable.
 *
 * Focus management uses `focus-trap-react` (#1261). The trap activates on
 * mount and releases on unmount. Esc dismiss is wired here AND in
 * TourProvider for redundancy.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TOOLTIP_WIDTH = 280;
const TOOLTIP_GUTTER = 12;
const VIEWPORT_PADDING = 16;
const MOBILE_BREAKPOINT = 640;

function isMobile() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

/**
 * Resolve final placement given the requested side and the anchor's
 * position. Auto-flips to the opposite side if the requested side would
 * push the tooltip off-screen.
 *
 * stepNumber: the current registry step number (1-based). Step 3 on
 * mobile docks to TOP instead of bottom so the coach textarea stays
 * unobstructed for `interactive: true` typing (#1316).
 *
 * #1314 (M4): on mobile, every OTHER step docks to whichever side of the
 * spotlit anchor has more viewport space instead of always bottom-docking.
 * The dashboard nav cards (steps 5–8) sit LOW on the page; a hard
 * bottom-dock pins the coachmark over the very card it's spotlighting. We
 * compare the anchor's vertical midpoint against the viewport midpoint:
 * anchor in the lower half → top-dock (clear space is above); anchor in
 * the upper half → bottom-dock (clear space is below). This is the mobile
 * analogue of the desktop auto-flip below.
 */
export function resolvePlacement(rect, requestedPlacement, stepNumber) {
  if (isMobile()) {
    // Step 3 is pinned to TOP unconditionally (#1316): the coach textarea
    // lives at the bottom of the viewport and must stay reachable for
    // `interactive: true` typing regardless of where the anchor sits.
    if (stepNumber === 3) return 'top-docked';
    const vh =
      typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 0;
    // Guard against a zero/absent viewport height (jsdom-less test env or
    // pre-layout) — fall back to the historical bottom-dock default.
    if (!vh || !rect) return 'bottom-docked';
    const anchorMidY = rect.top + rect.height / 2;
    // Anchor below the viewport midpoint → its clear space is ABOVE it, so
    // top-dock. Otherwise keep the bottom-dock default.
    return anchorMidY > vh / 2 ? 'top-docked' : 'bottom-docked';
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fits = {
    top: rect.top - 80 > VIEWPORT_PADDING,
    bottom: rect.bottom + 80 < vh - VIEWPORT_PADDING,
    left: rect.left - TOOLTIP_WIDTH - TOOLTIP_GUTTER > VIEWPORT_PADDING,
    right: rect.right + TOOLTIP_WIDTH + TOOLTIP_GUTTER < vw - VIEWPORT_PADDING,
  };
  if (fits[requestedPlacement]) return requestedPlacement;
  // Flip to opposite side, then fall back to bottom.
  const flipped = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[
    requestedPlacement
  ];
  if (flipped && fits[flipped]) return flipped;
  if (fits.bottom) return 'bottom';
  return 'bottom-docked';
}

// Approximate coachmark height for vertical clamping. The DOM measurement
// isn't available before mount and we don't want a flash-of-overflow on
// first paint — this constant matches the typical 3-line title + 2-line
// body + footer (~150–170px) and is plenty for clamping decisions.
const TOOLTIP_HEIGHT_ESTIMATE = 180;

function computeStyle(rect, placement) {
  if (typeof window === 'undefined') return { position: 'fixed' };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Horizontal clamp for bottom/top placements where the tooltip is
  // centered on the anchor. Anchors near the screen edge (e.g. top-right
  // close X) would push a centered tooltip off the viewport.
  const clampHorizontal = (centerX) => {
    const leftEdge = centerX - TOOLTIP_WIDTH / 2;
    return Math.max(
      VIEWPORT_PADDING,
      Math.min(vw - TOOLTIP_WIDTH - VIEWPORT_PADDING, leftEdge)
    );
  };
  // Vertical clamp for left/right placements where the tooltip is
  // centered on the anchor's vertical midpoint. For top-of-screen
  // anchors (X close button at y≈25) the centered tooltip extends
  // above the viewport; this returns a top-edge y that pins the
  // tooltip inside [VIEWPORT_PADDING, vh - height - VP].
  const clampVertical = (centerY) => {
    const topEdge = centerY - TOOLTIP_HEIGHT_ESTIMATE / 2;
    return Math.max(
      VIEWPORT_PADDING,
      Math.min(vh - TOOLTIP_HEIGHT_ESTIMATE - VIEWPORT_PADDING, topEdge)
    );
  };
  switch (placement) {
    case 'bottom-docked':
      return {
        position: 'fixed',
        left: VIEWPORT_PADDING,
        right: VIEWPORT_PADDING,
        bottom: VIEWPORT_PADDING,
        width: 'auto',
        maxWidth: 'unset',
      };
    case 'top-docked':
      // #1316: mobile step 3 docks to top so the coaching textarea
      // (interactive: true) at the bottom of the viewport remains
      // accessible for typing.
      return {
        position: 'fixed',
        left: VIEWPORT_PADDING,
        right: VIEWPORT_PADDING,
        top: VIEWPORT_PADDING,
        width: 'auto',
        maxWidth: 'unset',
      };
    case 'top':
      return {
        position: 'fixed',
        top: rect.top - TOOLTIP_GUTTER,
        left: clampHorizontal(rect.left + rect.width / 2),
        transform: 'translateY(-100%)',
      };
    case 'bottom':
      return {
        position: 'fixed',
        top: rect.bottom + TOOLTIP_GUTTER,
        left: clampHorizontal(rect.left + rect.width / 2),
      };
    case 'left':
      return {
        position: 'fixed',
        top: clampVertical(rect.top + rect.height / 2),
        left: rect.left - TOOLTIP_GUTTER,
        transform: 'translateX(-100%)',
      };
    case 'right':
    default:
      return {
        position: 'fixed',
        top: clampVertical(rect.top + rect.height / 2),
        left: rect.right + TOOLTIP_GUTTER,
      };
  }
}

export default function Coachmark({
  step,
  currentStepNumber,
  totalSteps,
  anchorEl,
  placement: requestedPlacement,
  onNext,
  onBack,
  onSkip,
  isFirst,
  isLast,
  // Optional override for the primary button label. Step 1 sets this
  // to "Start scenario →" since clicking advances tour AND starts the
  // welcome run via OnboardingContext.startWelcomeScenario.
  primaryLabel,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  // Render-tick state forces re-render so the style useMemo below
  // re-reads the anchor's current rect. Previous version cached style
  // in setState which got stale when scroll happened in an inner
  // scrollable container that didn't dispatch scroll events the
  // window-capture listener could see.
  const [renderTick, setRenderTick] = useState(0);

  useLayoutEffect(() => {
    if (!anchorEl) return undefined;
    const tick = () => setRenderTick((n) => n + 1);
    window.addEventListener('resize', tick);
    window.addEventListener('scroll', tick, true);

    // Also poll briefly after mount so inner-container scrolls that
    // resolve a beat AFTER the coachmark mounts (e.g. coaching view
    // auto-scrolls the eval column) still get caught. ~1s of frames
    // is enough to settle without keeping a forever-loop.
    let raf;
    let frame = 0;
    const settle = () => {
      tick();
      frame += 1;
      if (frame < 60) raf = requestAnimationFrame(settle);
    };
    raf = requestAnimationFrame(settle);

    return () => {
      window.removeEventListener('resize', tick);
      window.removeEventListener('scroll', tick, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [anchorEl]);

  const { resolvedPlacement, style } = useMemo(() => {
    if (!anchorEl) return { resolvedPlacement: requestedPlacement, style: {} };
    const rect = anchorEl.getBoundingClientRect();
    const placement = resolvePlacement(rect, requestedPlacement, step?.step);
    return { resolvedPlacement: placement, style: computeStyle(rect, placement) };
    // renderTick included so the memo re-runs when we bump it from
    // the layout effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, requestedPlacement, renderTick, step?.step]);

  // Focal Esc handler. TourProvider also has one but coachmark is the
  // active dialog so locality matters for screen readers.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onSkip();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSkip]);

  // Focus the primary CTA on mount so keyboard users land on Next in zero
  // tabs. Replaces focus-trap-react: the trap kept yanking focus back to
  // the coachmark even with allowOutsideClick:true, which made the
  // coaching textarea (step 3 anchor) reject typing while the tour was up.
  // Passive focus-on-mount is the right pattern for product tours —
  // tooltips don't own focus, they just suggest where to look next.
  useEffect(() => {
    const primary = containerRef.current?.querySelector('[data-tour-primary]');
    if (primary && typeof primary.focus === 'function') primary.focus();
  }, [step.step]);

  return (
    <div
      ref={containerRef}
      className={`tour-coachmark tour-coachmark--${resolvedPlacement}`}
      style={style}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`tour-coachmark-title-${step.step}`}
      aria-describedby={`tour-coachmark-body-${step.step}`}
      data-testid={`tour-coachmark-${step.step}`}
    >
        <div className="tour-coachmark__header">
          <span className="tour-coachmark__dot" aria-hidden="true" />
          <span className="tour-coachmark__step-of">
            {t('onboarding.tour.stepOf', {
              current: currentStepNumber,
              total: totalSteps,
              defaultValue: 'STEP {{current}} OF {{total}}',
            })}
          </span>
        </div>
        <h3
          id={`tour-coachmark-title-${step.step}`}
          className="tour-coachmark__title"
        >
          {t(step.titleKey, { defaultValue: step.titleDefault })}
        </h3>
        <p
          id={`tour-coachmark-body-${step.step}`}
          className="tour-coachmark__body"
        >
          {t(step.bodyKey, { defaultValue: step.bodyDefault })}
        </p>
        <div className="tour-coachmark__controls">
          <div className="tour-coachmark__progress" aria-hidden="true">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={`tour-coachmark__progress-dot${
                  i + 1 <= currentStepNumber ? ' is-filled' : ''
                }`}
              />
            ))}
          </div>
          <div className="tour-coachmark__buttons">
            {isFirst ? (
              <button
                type="button"
                className="tour-coachmark__btn tour-coachmark__btn--ghost"
                onClick={onSkip}
              >
                {t('onboarding.tour.skip', { defaultValue: 'Skip tour' })}
              </button>
            ) : (
              <button
                type="button"
                className="tour-coachmark__btn tour-coachmark__btn--ghost"
                onClick={onBack}
              >
                {t('onboarding.tour.back', { defaultValue: 'Back' })}
              </button>
            )}
            <button
              type="button"
              className="tour-coachmark__btn tour-coachmark__btn--primary"
              onClick={onNext}
              data-tour-primary="true"
            >
              {primaryLabel
                ? primaryLabel
                : isLast
                  ? t('onboarding.tour.done', { defaultValue: 'Done' })
                  : t('onboarding.tour.next', { defaultValue: 'Next' })}
            </button>
          </div>
        </div>
      </div>
  );
}
